import React, { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from './Field';
import { EndSelect } from './Field.styles';
import { currencyDecimals, currencySymbol } from '../../onboardingV2.locale';

const formatter = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });

/** 49900 → '49.900' (es-CO, no decimals). */
export const formatThousands = (value) =>
    value === '' || value === null || value === undefined ? '' : formatter.format(value);

/** '$ 49.900' → 49900; '' when there are no digits. Max 9 digits are kept for validation. */
export const parseDigits = (text) => {
    const digits = String(text || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    return digits ? Number(digits) : '';
};

const separatorsOf = (locale) => {
    try {
        const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
        return { decimal: (parts.find((p) => p.type === 'decimal') || {}).value || ',' };
    } catch {
        return { decimal: ',' };
    }
};

/**
 * Typed text → { value, text } for a currency with `decimals` fraction digits, grouped in `locale`.
 * '1234,5' (es, 2 decimals) → { value: 1234.5, text: '1.234,5' }. Keeps a trailing separator so the
 * merchant can keep typing the cents.
 */
export const parseMoney = (raw, decimals, locale) => {
    const { decimal } = separatorsOf(locale);
    const input = String(raw || '');
    const sepIndex = decimals > 0 ? input.lastIndexOf(decimal) : -1;
    const intDigits = (sepIndex === -1 ? input : input.slice(0, sepIndex)).replace(/\D/g, '').slice(0, 10);
    const fraction = sepIndex === -1 ? null : input.slice(sepIndex + 1).replace(/\D/g, '').slice(0, decimals);
    if (!intDigits && !fraction) return { value: '', text: '' };
    const grouped = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(intDigits || 0));
    const value = Number(`${intDigits || 0}${fraction ? `.${fraction}` : ''}`);
    return { value, text: fraction === null ? grouped : `${grouped}${decimal}${fraction}` };
};

const formatValue = (value, decimals, locale) =>
    value === '' || value === null || value === undefined
        ? ''
        : new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value);

/**
 * Price input formatted while typing (`$ 49.900 COP`). Stores a clean number (decimals only for
 * currencies that use them). With `currencyOptions` + `onCurrencyChange` the currency is a
 * selector. Meant for a react-hook-form `Controller`.
 */
const MoneyField = forwardRef(
    ({ value, onChange, onBlur, currency = 'COP', locale = 'es-CO', currencyOptions, onCurrencyChange, id, ...rest }, ref) => {
        const { t } = useTranslation();
        const decimals = useMemo(() => currencyDecimals(currency), [currency]);
        // While typing, keep the text as entered (e.g. '19,' before the cents); formatted on blur.
        const [typing, setTyping] = useState(null);
        const selectable = Array.isArray(currencyOptions) && currencyOptions.length > 0 && onCurrencyChange;

        return (
            <TextField
                ref={ref}
                id={id}
                type="text"
                inputMode={decimals > 0 ? 'decimal' : 'numeric'}
                autoComplete="off"
                prefix={currencySymbol(currency, locale)}
                suffix={selectable ? null : currency}
                endAdornment={
                    selectable ? (
                        <EndSelect>
                            <span aria-hidden="true">{currency}</span>
                            <i className="ri-arrow-down-s-line" aria-hidden="true" />
                            <select
                                id={`${id}-currency`}
                                value={currency}
                                onChange={(event) => onCurrencyChange(event.target.value)}
                                aria-label={t('SignUpV2.producto.currencyLabel')}
                            >
                                {currencyOptions.map((option) => (
                                    <option key={option.code} value={option.code}>
                                        {`${option.code} · ${option.name}`}
                                    </option>
                                ))}
                            </select>
                        </EndSelect>
                    ) : null
                }
                value={typing !== null ? typing : formatValue(value, decimals, locale)}
                onChange={(event) => {
                    const next = parseMoney(event.target.value, decimals, locale);
                    setTyping(next.text);
                    onChange(next.value);
                }}
                onBlur={(event) => {
                    setTyping(null);
                    if (onBlur) onBlur(event);
                }}
                {...rest}
            />
        );
    }
);

export default MoneyField;
